import React from 'react';
import {
  flattenObject,
  Obj,
  stringify
} from '../Object.lib';

/**
 * React useState hook wrapper.
 * 
 * @param {object} initialState   [optional] The usual state used by the original useState.
 * @param {object} observedState  [optional] Typically, to observe component props changes.
 * @returns {any[]}
 * 
 * @returned state - Current state.
 * @returned updateState - Promise, to make a partial state update.
 * @returned setState - Function, to overwrite the full state.
 * 
 * @see https://react.dev/reference/react/useState
 */
export default function useState(initialState, observedState) {
  const _initialState = Obj(initialState);
  const _observedState = Obj(observedState);
  const { debug = false } = _observedState;
  if (observedState) {
    Object.assign(_observedState,
      {
        ___observed___: stringify(flattenObject(_observedState)),
        ..._observedState
      }
    );
    Object.assign(_initialState, _observedState);
  }
  const [{ ___observed___, ..._state }, _setState] = React.useState(_initialState);
  const _updateState = async (update = { ___updatedAt___: Date.now() }) => {
    return new Promise((resolve) => {
      _setState((prevState) => (update = { ...prevState, ...update }));
      setTimeout(() => resolve(update));
    });
  };
  if (observedState) {
    setTimeout(
      async () => {
        if (___observed___ !== _observedState.___observed___) {
          if (debug) {
            console.debug(
              'useState: change observed',
              '\n', ___observed___,
              '\n', _observedState.___observed___
            );
          }
          await _updateState(_observedState)
        }
      }
    );
  }
  return [_state, _updateState, _setState];
}
